import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSignIn } from "@clerk/react";
import monitoring from "@/lib/monitoring";
import { checkEmailExists } from "@/services/auth_api";

export type SignInStep = "email" | "password";
export type SignInPending = "none" | "checking" | "password" | "oauth";
export type SignInEmailError = "none" | "noaccount" | string;

export type OauthOnlyState = {
  providers: string[];
};

export function useSignInFlow() {
  const { signIn } = useSignIn();
  const navigate = useNavigate();

  const [step, setStep] = useState<SignInStep>("email");
  const [email, setEmailRaw] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState<SignInPending>("none");
  const [emailError, setEmailError] = useState<SignInEmailError>("none");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [oauthOnly, setOauthOnly] = useState<OauthOnlyState | null>(null);

  const setEmail = (v: string) => {
    setEmailRaw(v);
    if (emailError !== "none") setEmailError("none");
    if (oauthOnly) setOauthOnly(null);
  };

  const clearErrors = () => {
    setEmailError("none");
    setPasswordError(null);
    setFormError(null);
    setOauthOnly(null);
  };

  const submitEmail = async (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (pending !== "none") return;

    const v = email.trim().toLowerCase();
    if (!v || !v.includes("@")) {
      setEmailError("Enter a valid work email.");
      return;
    }

    clearErrors();
    setPending("checking");
    try {
      const result = await checkEmailExists(v);
      if (!result.exists) {
        setEmailError("noaccount");
        return;
      }

      // Has password (with or without OAuth): advance to password step.
      // OAuth-only: surface the available providers and stay on the email step.
      if (result.hasPassword) {
        setStep("password");
      } else if (result.oauthProviders.length > 0) {
        setOauthOnly({ providers: result.oauthProviders });
      } else {
        // Edge: user exists but has neither password nor OAuth (shouldn't be
        // possible under normal Clerk config). Treat like a generic server
        // error so the user has somewhere to land.
        setFormError(
          "This account has no sign-in method configured. Please contact support.",
        );
      }
    } catch (err) {
      monitoring.error(
        "signin_email_exists_check_failed",
        err instanceof Error ? err : new Error(String(err)),
      );
      setEmailError("Couldn't reach the server. Please try again.");
    } finally {
      setPending("none");
    }
  };

  const submitPassword = async (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (pending !== "none") return;
    setPasswordError(null);
    setFormError(null);
    setPending("password");

    const { error } = await signIn.password({
      identifier: email.trim().toLowerCase(),
      password,
    });

    if (error) {
      const message = error.longMessage || error.message || "Sign-in failed.";
      if (
        error.code === "form_password_incorrect" ||
        error.code.startsWith("form_password")
      ) {
        setPasswordError(message);
      } else {
        setFormError(message);
      }
      setPending("none");
      return;
    }

    if (signIn.status === "complete") {
      const { error: finalizeError } = await signIn.finalize({
        navigate: ({ decorateUrl }) => {
          const url = decorateUrl("/");
          if (url.startsWith("http")) {
            window.location.href = url;
          } else {
            navigate(url, { replace: true });
          }
        },
      });
      if (finalizeError) {
        setFormError(
          finalizeError.longMessage ||
            finalizeError.message ||
            "Sign-in completed but finalize failed.",
        );
      }
      setPending("none");
      return;
    }

    setFormError(
      "Additional verification is required to complete sign-in. Please contact support.",
    );
    setPending("none");
  };

  const startOauth = async (provider: string) => {
    if (pending !== "none") return;
    clearErrors();
    setPending("oauth");

    const origin = window.location.origin;
    try {
      // sso() in @clerk/react 6.5.0 doesn't populate
      // externalVerificationRedirectURL; create() does. Revisit when Clerk lands
      // the FAPI revamp referenced by the `TODO @revamp-hooks` comment in their
      // shared types.
      // Clerk's `strategy` is a narrow literal union (OAuthStrategy). The
      // provider value is normalized on the server to a bare name (e.g.
      // "google"), so `oauth_${provider}` is always a valid Clerk strategy in
      // practice — if it isn't, Clerk surfaces a typed error at runtime.
      const { error } = await signIn.create({
        strategy: `oauth_${provider}` as "oauth_google",
        redirectUrl: `${origin}/sign-in/sso-callback`,
        actionCompleteRedirectUrl: `${origin}/`,
      });

      if (error) {
        monitoring.error(
          "signin_oauth_create_error",
          error instanceof Error ? error : new Error(String(error)),
          { extra: { provider } },
        );
        setFormError(
          error.longMessage || error.message || `${provider} sign-in failed.`,
        );
        setPending("none");
        return;
      }

      const target =
        signIn.firstFactorVerification?.externalVerificationRedirectURL;
      if (target) {
        window.location.href = target.toString();
        return;
      }

      monitoring.error(
        "signin_factor_error",
        new Error(`no externalVerificationRedirectURL after oauth_${provider} create`),
        {
          extra: {
            provider,
            status: signIn.status,
            supportedFirstFactors: signIn.supportedFirstFactors,
            firstFactorVerification: signIn.firstFactorVerification,
          },
        },
      );
      setFormError(
        `Couldn't start ${provider} sign-in. Check the browser console for details.`,
      );
      setPending("none");
    } catch (err) {
      monitoring.error(
        "signin_oauth_threw",
        err instanceof Error ? err : new Error(String(err)),
        { extra: { provider } },
      );
      setFormError(err instanceof Error ? err.message : `${provider} sign-in failed.`);
      setPending("none");
    }
  };

  const backToEmail = () => {
    setStep("email");
    setPassword("");
    setPasswordError(null);
    setFormError(null);
  };

  return {
    step,
    email,
    setEmail,
    password,
    setPassword,
    pending,
    emailError,
    passwordError,
    formError,
    oauthOnly,
    submitEmail,
    submitPassword,
    startOauth,
    backToEmail,
  };
}

export type SignInFlow = ReturnType<typeof useSignInFlow>;
